import TYPES from '@/types';

// Application
import GetGlobalTrackingQuery from '@/modules/flagship/investor-goal/global-tracking/application/queries/get-global-tracking-query';

// Domain
import { GlobalTrackingEntity } from '@/modules/flagship/investor-goal/global-tracking/domain/entities/global-tracking-entity';
import Inject from '@/modules/shared/domain/di/inject';
import { MessageNotifier } from '@/modules/shared/domain/notifiers/message_notifier';
import { Router } from '@/modules/shared/domain/router';
import Translator from '@/modules/shared/domain/i18n/translator';
import { Values } from '@/modules/shared/domain/i18n/types';
import CustomerOverviewService from '@/modules/home-dashboard/customer-overview/application/services/customer-overview-service';
import CustomerOverviewState from '@/modules/home-dashboard/customer-overview/domain/state/customer-overview-state';

export default class FlagshipTrackingGoalsCardViewModel {
  @Inject(TYPES.CUSTOMER_OVERVIEW_SERVICE)
  private readonly customer_overview_service!: CustomerOverviewService;

  @Inject(TYPES.GET_GLOBAL_TRACKING_QUERY)
  readonly get_global_tracking_query!: GetGlobalTrackingQuery;

  @Inject(TYPES.ROUTER)
  private readonly router!: Router;

  @Inject(TYPES.NOTIFIER)
  readonly message_notifier!: MessageNotifier;

  @Inject(TYPES.I18N)
  readonly translator!: Translator;

  readonly i18n_namespace = 'components.my-investment-dashboard.flagship-tracking-goals-card';

  is_loading = false;

  progress_message = '';

  global_tracking: GlobalTrackingEntity = {
    global_tracking_percent: 0,
    current_global_balance: 0,
    expected_global_balance: 0,
    total_goals_with_tracking_enabled: 0,
  };

  has_active_goals = false;

  has_active_goals_tracking_disabled = false;

  customer_overview: CustomerOverviewState = this.customer_overview_service.get_customer_overview();

  translate = (message: string, values?: Values) => this.translator.translate(`${this.i18n_namespace}.${message}`, values);

  get background_image() {
    // eslint-disable-next-line global-require, @typescript-eslint/no-var-requires
    const bgImage = require('@/assets/images/investment-dashboard/goals_card.svg');
    return {
      backgroundImage: `url(${bgImage})`,
      backgroundRepeat: 'no-repeat',
      backgroundPositionX: 'right',
      backgroundPositionY: 'bottom',
      backgroundSize: 'contain',
    };
  }

  onCardCompletedClick = () => {
    if (!this.customer_overview.item.goals_overview.active_goals) {
      this.router.navigate('/goals-dashboard?create_goals=true');
    } else {
      this.router.navigate('/goals-dashboard');
    }
  };

  loadGlobalTracking = async () => {
    try {
      this.global_tracking = await this.get_global_tracking_query.execute();
      this.has_active_goals = this.customer_overview.item.goals_overview
        .active_goals_with_tracking > 0;
      this.has_active_goals_tracking_disabled = this.customer_overview.item.goals_overview
        .active_goals_without_tracking > 0;
      this.global_tracking.global_tracking_percent = (
        this.global_tracking.global_tracking_percent > 100
      )
        ? 100
        : Number(this.global_tracking.global_tracking_percent.toFixed(0));
      this.setGoalProgressStatus(this.global_tracking.global_tracking_percent);
    } catch (e) {
      this.message_notifier.showErrorNotification(this.translate('errors.get_global_tracking_query'));
    }
  }

  setGoalProgressStatus = (percent: number) => {
    switch (true) {
      case percent >= 80:
        this.progress_message = this.translate('going_according_to_plan');
        break;
      case (percent >= 65 && percent < 80):
        this.progress_message = this.translate('deviated_slightly_from_your_plan');
        break;
      case (percent >= 50 && percent < 65):
        this.progress_message = this.translate('deviated_from_your_plan');
        break;
      default:
        this.progress_message = this.translate('less_than_half_of_planned');
        break;
    }
  }

  initialize = async () => {
    await this.loadGlobalTracking();
  }
}
